import { fetchWithTimeout, Timeout, Timespan, toJson } from './Common';
import { AuthTokenProvider, IClientDescription, IRegistrarClient, IRegistrarClientOptions, ITelemetrySender,
         ITransports, IUrlRewrite, LogProvider, TelemetryEvent, TokenResult } from './Interfaces';

const SUPPORTED_TOKEN_TYPES = <const>['skype', 'aad', 'cae'];

/**
 * Thrown when retry process was cancelled
 */
class CancelationError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'CancelationError';
    }
}

/**
 * Randomized exponential delay used for retries
 */
class ExponentialDelay {
    private static idCounter: number = 0;

    public readonly id: number;

    private backoffCount: number = 0;
    private timerHandle?: Timeout;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private cancelFunc: ((err?: any) => void) | undefined;

    public constructor(
        private logger: LogProvider,
        private maxBackoffInMs: number,
        private initialDelay: number
    ) {
        this.id = ++ExponentialDelay.idCounter;
    }

    public delay(taskName: string): Promise<void> {
        if (this.timerHandle !== undefined) {
            throw new Error('Retry sequence logical failure');
        }

        if (this.backoffCount === -1) {
            return new Promise((resolve, reject) => { reject(new CancelationError('Cancelled')); });
        }

        const backoffMs = this.calculateNextBackoffMs();
        this.backoffCount++;
        this.logger.info(`[RegistrarClient] Backing off ${taskName} for ${backoffMs} milliseconds with ID ${this.id}`);

        return new Promise( (resolve, reject) => {
            this.cancelFunc = reject;
            this.timerHandle = setTimeout(() => {
                this.logger.info(`[RegistrarClient] Back off for ${taskName} with ID ${this.id} complete`);
                this.timerHandle = undefined;
                resolve();
            }, backoffMs);
        });
    }

    public cancel(): void {
        if (this.timerHandle !== undefined) {
            this.logger.debug('Resetting back off');
            clearTimeout(this.timerHandle);
            if (this.cancelFunc !== undefined) {
                this.cancelFunc(new CancelationError('Cancelled'));
            }
        }
        this.backoffCount = -1;
    }

    private calculateNextBackoffMs(): number {
        const fudge = 1 + (Math.random() - 0.5) * 0.4; // +/- 20%
        let backoff = this.initialDelay * Math.pow(2, this.backoffCount) * fudge;
        backoff = Math.round(backoff);

        return Math.min(this.maxBackoffInMs, backoff);
    }
}

/**
 * Registrar client implementation
 */
export class RegistrarClient implements IRegistrarClient {
    private backoffs: { [id: string]: ExponentialDelay };
    private eventLogger?: ITelemetrySender;
    private cachedRegistrationParams?: [IClientDescription, ITransports];
    private maxBackOffTime: number;
    private maxRetriesForGetToken: number;
    private readonly DEFAULT_MAX_RETRIES_FOR_GET_TOKEN = 15;
    private readonly proxyUrlRewrite: IUrlRewrite;

    // Default value for maximum backoff between retries
    private readonly DEFAULT_MAX_BACKOFF_TIME_IN_MS = 300000; // 5 minutes

    constructor(
        private logger: LogProvider,
        private tokenProvider: AuthTokenProvider,
        private options: IRegistrarClientOptions
    ) {
        this.backoffs = {};
        this.maxBackOffTime =
          this.options.maxRetryDelayMs > 0
            ? this.options.maxRetryDelayMs
            : this.DEFAULT_MAX_BACKOFF_TIME_IN_MS;
        this.maxRetriesForGetToken =
          options.maxRetriesForGetToken === undefined ||
          options.maxRetriesForGetToken === null
            ? this.DEFAULT_MAX_RETRIES_FOR_GET_TOKEN
            : options.maxRetriesForGetToken;
        this.proxyUrlRewrite = options.proxyUrlRewrite ?? (url => url);
    }

    public setTelemetryLogger(eventLogger: ITelemetrySender) {
        this.eventLogger = eventLogger;
    }

    public async register(client: IClientDescription, transports: ITransports): Promise<void> {
        await this.performRegistration(client, transports, 'pr_set_registration');
        this.cachedRegistrationParams = [client, transports];
    }

    public async unregister(): Promise<void> {
        this.logger.info('[RegistrarClient] sending unregister request');

        const unregisterRequest = new Request(
            this.proxyUrlRewrite(`${this.options.registrarUrl}/${this.options.registrationId}`),
            {
                method: 'DELETE',
                mode: 'cors',
                headers: new Headers({
                    ...this.options.extraRegistrationHeaders,
                    accept: 'application/json, text/javascript'
                })
            });

            await this.callRegistrar(unregisterRequest, 'pr_delete_registration');
    }

    public cancelPendingRequests() {
        Object.keys(this.backoffs).forEach((id: string) => {
            this.backoffs[id].cancel();
        });
        this.backoffs = {};
    }

    public async resendRegistration(): Promise<void> {
        if (!this.cachedRegistrationParams) {
            throw new Error('Re-registration failed because there is no registration parameters cached');
        }

        await this.performRegistration(this.cachedRegistrationParams[0], this.cachedRegistrationParams[1], 'pr_resend_registration');
    }

    private async performRegistration(client: IClientDescription, transports: ITransports, eventName: string) {
        this.logger.info('[RegistrarClient] Sending register request');
        const payload = {
            clientDescription: client,
            registrationId: this.options.registrationId,
            nodeId: '',
            transports: transports
        };
        const registerRequest = new Request(this.proxyUrlRewrite(this.options.registrarUrl), {
            method: 'POST',
            mode: 'cors',
            headers: new Headers({
                ...this.options.extraRegistrationHeaders,
                'content-type': 'application/json',
                accept: 'application/json, text/javascript'
            }),
            body: toJson(payload)
        });
        await this.callRegistrar(registerRequest, eventName);
    }

    private startBackoff() {
        const backoff = new ExponentialDelay(
          this.logger,
          this.maxBackOffTime,
          this.options.initialRetryDelayMs
        );
        this.backoffs[backoff.id] = backoff;

        return backoff;
    }

    private stopBackoff(backoff: ExponentialDelay) {
        backoff.cancel();
        delete this.backoffs[backoff.id];
    }

    private async getToken(claimsChallenge: string|undefined): Promise<TokenResult> {
        const backoff = this.startBackoff();
        let retryCount = 0;
        for (;;) {
            try {
                this.logger.info('[RegistrarClient] Asking for a new token');
                const token = await this.tokenProvider({
                    needFresh: true,
                    supportedTokenTypes: SUPPORTED_TOKEN_TYPES,
                    wwwAuthenticateHeader: claimsChallenge,
                    purpose: 'registrar'
                });
                this.stopBackoff(backoff);

                return token;
            } catch (error) {
                try {
                    retryCount++;
                    const stringfiedError = JSON.stringify(error);
                    if (retryCount > this.maxRetriesForGetToken) {
                        const err = `[RegistrarClient] getToken retry limit hit. Will not retry now. Error: ${stringfiedError}`;
                        this.logger.error(err);
                        this.stopBackoff(backoff);

                        return Promise.reject(err);
                    }

                    this.logger.warn(`[RegistrarClient] Retrying for a new token. Retry Count: ${retryCount} Error: ${stringfiedError}`);
                    await backoff.delay('Fetching a new token');
                    continue;
                } catch (cancelError) {
                    this.stopBackoff(backoff);
                    throw cancelError;
                }
            }
        }
    }

    private async callRegistrar(request: Request, eventName: string): Promise<Response> {
        const backoff = this.startBackoff();

        const cachedToken = await this.tokenProvider({
            needFresh: false,
            supportedTokenTypes: SUPPORTED_TOKEN_TYPES,
            wwwAuthenticateHeader: undefined,
            purpose: 'registrar'
        });
        this.setTokenHeader(request, cachedToken);

        const stopwatch = new Timespan();
        let retryCount = 0;

        for (;;) {
            let response: Response | undefined;
            try {
                // Make a clone in case we need to retry, one Request cannot be used twice with fetch()
                const req = request.clone();
                response = await fetchWithTimeout(req, this.options.requestTimeoutMs);
                if (response.status === 401) {
                    retryCount++;
                    if (retryCount > this.maxRetriesForGetToken) {
                        const err = `[RegistrarClient] getSkypeToken retry limit hit. Will not retry now. Request '${request.url}' failed with ${response.status} ${response.statusText}`;
                        this.logger.error(err);
                        this.stopBackoff(backoff);

                        return Promise.reject(err);
                    }

                    this.logger.warn(`[RegistrarClient] Retry Count ${retryCount}. Request '${request.url}' failed with ${response.status} ${response.statusText}`);
                    const claimsChallenge = response.headers?.get('www-authenticate') ?? undefined;
                    this.setTokenHeader(request, await this.getToken(claimsChallenge));
                    // do not wait the first time - quick reaction to e.g. expired token
                    if (retryCount > 1) {
                        // for later retries, e.g. with token revocation, wait until SEAL knows the token was revoked,
                        // so that proper claims challenges are sent, see https://skype.visualstudio.com/SCC/_workitems/edit/3337904/
                        await backoff.delay('Registrar call retry after 401');
                    }
                    continue;
                } else if (response.status >= 500 && response.status < 600) {
                    // throw and retry after in the catch section
                    throw new Error(`Fetch for '${request.url}' failed with ${response.status} ${response.statusText}`);
                }
            } catch (error) {
                this.logger.error(`[RegistrarClient] Request failed with ${error}`);
                try {
                    // retry on network errors (thrown by fetch) and retryable codes
                    await backoff.delay('Registrar call retry');
                    continue;
                } catch (canceledError) {
                    this.logger.error('[RegistrarClient] Request cancelled');
                    this.stopBackoff(backoff);
                    throw canceledError;
                }
            } finally {
                this.sendTelemetryEvent(eventName, request, response, stopwatch);
            }

            this.stopBackoff(backoff);
            if (response.ok) {
                return response;
            } else  {
                let details: string;
                try {
                    details = JSON.stringify(await response.json());
                } catch (error) {
                    details = 'no details';
                }
                // Note MS-CV will be null until Registrar service CORS fix
                // https://dev.azure.com/skype/SCC/_workitems/edit/2353812
                const message = `Fetch for '${request.url}' failed with ${response.status} ${response.statusText} (${details}, MS-CV: ${response.headers?.get('MS-CV')})`;
                this.logger.error(`[RegistrarClient] ${message}`);
                throw new Error(message);
            }
        }
    }

    private setTokenHeader(request: Request, token: TokenResult) {
        // Because a request object is cloned on 401 retries, we need to reset the state to baseline
        request.headers.delete('X-Skypetoken');
        request.headers.delete('Authorization');
        request.headers.delete('X-MS-Migration');

        switch (token.tokenType.toLowerCase()) {
            case 'skype':
                request.headers.set('X-Skypetoken', token.token);
                break;

            case 'aad':
            case 'cae':
                request.headers.set('Authorization', `Bearer ${token.token}`);
                break;

            default:
                throw new Error(`unsupported token type: ${token.tokenType}`);
        }
        if (this.options.usingLegacyTokenApi === false) {
            request.headers.set('X-MS-Migration', 'True');
        }
    }

    private sendTelemetryEvent(eventName: string, request: Request, response: Response | undefined, stopwatch: Timespan) {
        if (this.eventLogger === undefined) {
            return;
        }

        const clientEvent: TelemetryEvent = {
            name: eventName,
            properties: {
                url: { value: request.url },
                result_code: { value: response !== undefined ? response.status : 0 },
                begin_timestamp: { value: stopwatch.startTime },
                elapsed: { value: stopwatch.duration }
            }
        };

        this.eventLogger.logEvent(clientEvent);
    }
}

export function createRegistrarClient(
    logProvider: LogProvider,
    tokenProvider: AuthTokenProvider,
    options: IRegistrarClientOptions
): IRegistrarClient {
    return new RegistrarClient(logProvider, tokenProvider, options);
}



// WEBPACK FOOTER //
// ./lib_temp/RegistrarClient.ts